import { createItem, deleteItem, getItem, listItems, updateItem } from './sharepoint';

const LIST = 'team';

export const EMPLOYMENT_TYPES = ['Employee', 'Contractor', 'Sub-Contractor'];
export const EMPLOYMENT_STATUSES = ['Active', 'Exited', 'On Leave'];
export const PORTAL_ROLES = [
  'Director',
  // Executive = Proxemics staff who sit above plant-level work: they have no
  // manager above them and aren't assigned to a plant. See SPEC_roles-and-
  // permissions.md (all-plant visibility, view-only).
  'Executive',
  'RegionalManager',
  'LiaisonManager',
  'Liaison',
  'None',
];

/**
 * Emergency contact relation options offered on TeamForm and on My
 * Account's "Update Emergency Contact" form. "Other" is the escape
 * hatch — forms render a free-text field when it's picked so anything
 * outside the list (Friend, Grandparent, etc.) still flows through
 * without a schema change.
 *
 * Stored as plain text on the Team record (EmergencyContactRelation).
 */
export const EMERGENCY_CONTACT_RELATIONS = [
  'Spouse',
  'Partner',
  'Parent',
  'Child',
  'Other',
];

function parseJson(raw, fallback) {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

/**
 * Translate the raw SharePoint row into the shape the UI wants:
 *   plantCodes:       ['SHAP', 'WTAP']
 *   liaisonManagers:  ['Alex Smith', 'Jamie Doe']
 *
 * Legacy rows may have only `PlantCode` (single) instead of PlantCodesJson —
 * synthesize a one-element array in that case so the UI never sees a "half
 * state." Writes go the other direction via encode() so both representations
 * stay consistent.
 */
function decode(row) {
  if (!row) return row;
  let plantCodes = parseJson(row.PlantCodesJson, null);
  if (!plantCodes || !Array.isArray(plantCodes)) {
    plantCodes = row.PlantCode ? [row.PlantCode] : [];
  }
  let liaisonManagers = parseJson(row.LiaisonManagersJson, null);
  if (!liaisonManagers || !Array.isArray(liaisonManagers)) {
    liaisonManagers = [];
  }
  // Per-plant supplier support for Liaisons (added July 2026):
  // { PLANTCODE: ['AUTOSYS', 'ABC1'], ... }. Stored in the
  // SupplierAssignmentsJson column — NOTE: that column must exist on
  // the TeamMembers list or SharePoint silently drops it on save.
  let supplierAssignments = parseJson(row.SupplierAssignmentsJson, null);
  if (
    !supplierAssignments ||
    typeof supplierAssignments !== 'object' ||
    Array.isArray(supplierAssignments)
  ) {
    supplierAssignments = {};
  }
  // When the member's address / phone / emergency contact were last
  // changed (added Oct 2026 for the My Account card footer):
  // { address: ISO, phone: ISO, emergencyContact: ISO }. Stamped
  // automatically by updateTeamMember(); see stampContactUpdates().
  // NOTE: the ContactUpdatedJson column must exist on the TeamMembers
  // list (run scripts/provision-sharepoint-lists.mjs) or SharePoint
  // silently drops it on save.
  let contactUpdated = parseJson(row.ContactUpdatedJson, null);
  if (!contactUpdated || typeof contactUpdated !== 'object' || Array.isArray(contactUpdated)) {
    contactUpdated = {};
  }
  return { ...row, plantCodes, liaisonManagers, supplierAssignments, contactUpdated };
}

// ---- "Last updated" stamps for the contact groups ----------------------
//
// Which stored columns make up each group shown on My Account. A change
// to ANY column in a group stamps that group with the save time.
export const CONTACT_UPDATE_GROUPS = {
  address: [
    'MailingStreet',
    'MailingStreet2',
    'MailingStreet3',
    'MailingCity',
    'MailingState',
    'MailingZip',
    'MailingCountry',
  ],
  phone: ['Phone'],
  emergencyContact: [
    'EmergencyContactName',
    'EmergencyContactPhone',
    'EmergencyContactRelation',
  ],
};

// Phone columns compare on digits only so re-saving "(810) 923-1100"
// over "8109231100" isn't counted as a change.
const norm = (field, v) =>
  /Phone$/.test(field)
    ? String(v ?? '').replace(/[^\d+]/g, '')
    : String(v ?? '').trim();

/**
 * Compare the record as it was (`previous`) with the record about to be
 * saved (`next`) and return the contactUpdated map with a fresh ISO
 * timestamp on every group whose fields changed. Groups that didn't
 * change keep their old stamp. Pure; exported for tests.
 *
 * `previous` may be null (brand-new record): then nothing is stamped,
 * since "last updated" on a record that was just created would only
 * ever echo the creation date.
 */
export function stampContactUpdates(previous, next, now = new Date()) {
  const base =
    (next?.contactUpdated && typeof next.contactUpdated === 'object' && next.contactUpdated) ||
    (previous?.contactUpdated && typeof previous.contactUpdated === 'object' && previous.contactUpdated) ||
    {};
  const out = { ...base };
  if (!previous) return out;
  const iso = now.toISOString();
  for (const [group, fields] of Object.entries(CONTACT_UPDATE_GROUPS)) {
    // Only fields the caller actually sent count — a partial patch that
    // omits a column isn't "clearing" it.
    const changed = fields.some(
      (f) => f in (next || {}) && norm(f, next[f]) !== norm(f, previous[f]),
    );
    if (changed) out[group] = iso;
  }
  return out;
}

/**
 * Serialize the UI-friendly `plantCodes` / `liaisonManagers` arrays back into
 * their JSON columns, and mirror the first plant to the legacy single-value
 * columns so the existing index-page column (which reads PrimaryPlant) keeps
 * working.
 *
 * The caller is responsible for also setting PrimaryPlant to the plant's
 * display name — we don't have the Plants list here, so we can only copy
 * the code, not resolve it to the title.
 */
function encode(model) {
  // `id` is a SharePoint system field — it identifies the item in the URL
  // and should never appear inside the `fields` payload. Also pull out the
  // array-shaped UI fields that need their own JSON-column encoding.
  const {
    plantCodes,
    liaisonManagers,
    supplierAssignments,
    contactUpdated,
    id: _id,
    ...rest
  } = model;
  const codes = Array.isArray(plantCodes) ? plantCodes.filter(Boolean) : [];
  const lms = Array.isArray(liaisonManagers)
    ? liaisonManagers.map((s) => (s || '').trim()).filter(Boolean)
    : [];
  // Clean the per-plant supplier map: uppercase keys/codes, drop empties,
  // dedupe. Only meaningful for Liaisons — the form clears it for other
  // roles before save.
  const sa = {};
  if (supplierAssignments && typeof supplierAssignments === 'object') {
    for (const [k, v] of Object.entries(supplierAssignments)) {
      const key = String(k || '').toUpperCase().trim();
      const list = Array.isArray(v)
        ? Array.from(
            new Set(
              v.map((c) => String(c || '').toUpperCase().trim()).filter(Boolean),
            ),
          )
        : [];
      if (key && list.length) sa[key] = list;
    }
  }
  return {
    ...rest,
    PlantCodesJson: JSON.stringify(codes),
    PlantCode: codes[0] || '',
    LiaisonManagersJson: JSON.stringify(lms),
    SupplierAssignmentsJson: JSON.stringify(sa),
    ContactUpdatedJson: JSON.stringify(
      contactUpdated && typeof contactUpdated === 'object' ? contactUpdated : {},
    ),
  };
}

/**
 * Ensure Title stays in sync with FirstName + LastName so the index page
 * search, sort, and legacy consumers (which read Title) all see the same
 * canonical display name. Caller can still pass an explicit Title; we only
 * fill it in when blank.
 */
function withSyncedTitle(model) {
  const first = (model.FirstName || '').trim();
  const last = (model.LastName || '').trim();
  const composed = [first, last].filter(Boolean).join(' ');
  if (composed && !(model.Title && model.Title.trim())) {
    return { ...model, Title: composed };
  }
  return model;
}

export async function listTeam(opts) {
  const rows = await listItems(LIST, { orderby: 'fields/LastName', ...opts });
  return rows.map(decode);
}

export const getTeamMember = async (id) => decode(await getItem(LIST, id));
export const createTeamMember = (model) =>
  createItem(LIST, encode(withSyncedTitle(model))).then(decode);

/**
 * Update a team member, then cascade any display-name change to everyone
 * else on the roster who references them by name (RegionalManager /
 * liaisonManagers). Without this, renaming someone leaves stale string
 * copies on the people they manage — the Team index and Team Member
 * Detail pages both read those strings verbatim.
 *
 * Order of operations:
 *   1. Fetch the member's current row so we know the OLD name.
 *   2. Write the new row.
 *   3. If the composed display name changed, sweep the roster and patch
 *      any row whose RegionalManager matches the old name, or whose
 *      liaisonManagers array contains the old name.
 *
 * The cascade is best-effort: a failed patch on a single downstream row
 * is logged and swallowed so the primary update still succeeds.
 */
export async function updateTeamMember(id, model) {
  // Fetch the current row first: it gives us the OLD name for the rename
  // cascade below, and the old contact fields so we can stamp "last
  // updated" on whichever of address / phone / emergency contact this
  // save actually changes (shown in the My Account card footer). If the
  // fetch fails we still save, just without stamping.
  const previous = await getItem(LIST, id)
    .then(decode)
    .catch(() => null);
  const oldName = previous ? fullName(previous) : '';
  const stamped = {
    ...model,
    contactUpdated: stampContactUpdates(previous, model),
  };
  const payload = encode(withSyncedTitle(stamped));
  const updatedRaw = await updateItem(LIST, id, payload);
  const updated = decode(updatedRaw);
  const newName = fullName(updated);
  if (oldName && newName && oldName !== newName) {
    await renameMemberReferences(oldName, newName, id).catch((err) => {
      // Non-fatal: the member's own record was saved; we just failed
      // to propagate. Log so a director sees it in the console.
      // eslint-disable-next-line no-console
      console.error('[team] rename cascade failed', err);
    });
  }
  return updated;
}

export const deleteTeamMember = (id) => deleteItem(LIST, id);

/**
 * Sweep the team roster and replace every occurrence of `oldName` with
 * `newName` in the RegionalManager / liaisonManagers fields. Skips the
 * row at `excludeId` (the person being renamed — their own references
 * should be left alone).
 *
 * Returns the number of rows patched so callers can log or surface it.
 * Callable directly for one-off cleanups where a name was changed
 * before this cascade existed.
 */
export async function renameMemberReferences(oldName, newName, excludeId) {
  const old = (oldName || '').trim();
  const next = (newName || '').trim();
  if (!old || !next || old === next) return 0;
  const rows = await listItems(LIST);
  let patched = 0;
  for (const row of rows) {
    if (excludeId != null && row.id === excludeId) continue;
    const decoded = decode(row);
    const fields = {};
    if ((decoded.RegionalManager || '').trim() === old) {
      fields.RegionalManager = next;
    }
    const lms = Array.isArray(decoded.liaisonManagers)
      ? decoded.liaisonManagers
      : [];
    if (lms.some((n) => (n || '').trim() === old)) {
      const rewritten = lms.map((n) =>
        (n || '').trim() === old ? next : n,
      );
      fields.LiaisonManagersJson = JSON.stringify(rewritten);
    }
    if (Object.keys(fields).length === 0) continue;
    try {
      // eslint-disable-next-line no-await-in-loop
      await updateItem(LIST, row.id, fields);
      patched += 1;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[team] failed to rename references on row', row.id, err);
    }
  }
  return patched;
}


function fullName(m) {
  if (!m) return '';
  const composed = [m.FirstName, m.LastName].filter(Boolean).join(' ').trim();
  if (composed) return composed;
  return (m.Title || '').trim();
}

/**
 * Tenure in whole years between startDate and either endDate or today
 * (whichever is earlier if endDate is in the past). Returns null if startDate
 * is missing/invalid — callers should render "—" in that case.
 */
export function computeTenureYears(startDate, endDate) {
  if (!startDate) return null;
  const start = new Date(startDate);
  if (Number.isNaN(start.getTime())) return null;
  const end = endDate ? new Date(endDate) : new Date();
  if (Number.isNaN(end.getTime())) return null;
  const ms = end - start;
  if (ms < 0) return 0;
  // 365.25 smooths over leap years; floor so "11 months in" shows as 0 years.
  return Math.floor(ms / (1000 * 60 * 60 * 24 * 365.25));
}
